import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
/**
 * Card payments taken in 3x's storefront through Bambora (Worldline) Online Checkout are
 * authorised there and captured here when the order ships. One gateway per Bambora merchant
 * number; the order carries the merchant number and transaction id from the 3x feed.
 */
export const gatewayMode = z.enum(['dry_run', 'live']);
export const paymentGatewayDto = z.object({
    id: uuid,
    provider: z.literal('bambora'),
    merchantNumber: z.string(),
    name: z.string(),
    mode: gatewayMode,
    baseUrl: z.string().nullable(),
    /** Masked when set. */
    accessToken: z.string(),
    secretToken: z.string(),
    /** Checks Online Checkout callbacks (storefront card payments). */
    md5Key: z.string(),
    active: z.boolean(),
    orderCount: z.number().int(),
    updatedAt: isoDateTime,
});
export const savePaymentGatewayRequest = z.object({
    merchantNumber: z.string().trim().min(3).max(30),
    name: z.string().trim().max(100).default(''),
    mode: gatewayMode.default('dry_run'),
    baseUrl: z.string().trim().max(300).nullable().optional(),
    accessToken: z.string().trim().max(200).default(''),
    secretToken: z.string().trim().max(200).default(''),
    md5Key: z.string().trim().max(200).default(''),
    active: z.boolean().default(true),
});
export const captureStatus = z.enum(['captured', 'dry_run', 'failed', 'skipped']);
export const paymentCaptureDto = z.object({
    id: uuid,
    amountMinor: z.number().int(),
    currency: z.string(),
    status: captureStatus,
    trigger: z.enum(['ship', 'manual']),
    mode: gatewayMode.nullable(),
    message: z.string().nullable(),
    actorName: z.string(),
    at: isoDateTime,
});
/** The order page's payment card. */
export const orderPaymentDto = z.object({
    orderId: uuid,
    provider: z.string(),
    merchantNumber: z.string().nullable(),
    transactionId: z.string().nullable(),
    totalMinor: z.number().int(),
    currency: z.string(),
    /** Captured by us (live captures only). */
    capturedMinor: z.number().int(),
    /** What Bambora says, when a live gateway could be asked. */
    remote: z.object({ authorizedMinor: z.number().int(), capturedMinor: z.number().int(), creditedMinor: z.number().int(), availableMinor: z.number().int() }).nullable(),
    remoteError: z.string().nullable(),
    gateway: z.object({ id: uuid, name: z.string(), mode: gatewayMode }).nullable(),
    capturable: z.boolean(),
    captures: z.array(paymentCaptureDto),
});
export const captureRequest = z.object({ amountMinor: z.number().int().min(1).optional() });
export const emailStatus = z.enum(['queued', 'sent', 'failed', 'not_sent', 'demo']);
export const emailDto = z.object({
    id: uuid,
    kind: z.string(),
    to: z.string(),
    subject: z.string(),
    status: emailStatus,
    error: z.string().nullable(),
    orderId: uuid.nullable(),
    orderNumber: z.string().nullable(),
    createdAt: isoDateTime,
    sentAt: isoDateTime.nullable(),
});
export const emailQuery = z.object({
    status: emailStatus.optional(),
    orderId: uuid.optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const testEmailRequest = z.object({ to: z.string().trim().email() });
//# sourceMappingURL=payments.js.map